/**
 * Timed Release Timer Web Component
 * 
 * Custom element that displays a countdown timer for timed release products.
 * Dispatches 'timed-release:expired' event when timer reaches zero.
 * 
 * Usage:
 * <timed-release-timer release-time="2024-12-25T10:00:00+11:00" product-id="123456"></timed-release-timer>
 */
class TimedReleaseTimer extends HTMLElement {
  constructor() {
    super();
    this.releaseTime = null;
    this.productId = null;
    this.intervalId = null;
    this.elements = {};
    this.multipliers = {
      second: 1000,
      minute: 60 * 1000,
      hour: 60 * 60 * 1000,
      day: 24 * 60 * 60 * 1000
    };
  }

  connectedCallback() {
    // Get attributes
    const releaseTimeAttr = this.getAttribute('release-time');
    this.productId = this.getAttribute('product-id');

    if (!releaseTimeAttr) {
      console.warn('TimedReleaseTimer: No release-time attribute provided');
      return;
    }

    // Parse release time
    // Liquid's %z format produces +1100 (no colon), but JavaScript Date expects +11:00 (with colon)
    // Normalize the timezone format before parsing
    let normalizedTime = releaseTimeAttr;
    
    // Convert timezone format from +1100 to +11:00 (add colon if missing)
    // Match pattern: [+-] followed by 4 digits at the end (timezone without colon)
    normalizedTime = normalizedTime.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3');
    
    // Parse the normalized date string
    let releaseDate = new Date(normalizedTime);
    
    // If parsing still fails, try alternative formats
    if (isNaN(releaseDate.getTime())) {
      // Try parsing as ISO 8601 without timezone (assume UTC)
      const withoutTz = normalizedTime.replace(/[+-]\d{2}:\d{2}$/, '').replace(/Z$/, '');
      releaseDate = new Date(withoutTz + 'Z'); // Add Z to indicate UTC
    }
    
    this.releaseTime = releaseDate.getTime();

    if (isNaN(this.releaseTime)) {
      console.error('TimedReleaseTimer: Invalid release-time format', releaseTimeAttr);
      return;
    }

    // Check if release time is in the past - if so, don't initialize timer
    const now = new Date().getTime();
    if (this.releaseTime <= now) {
      // Release time has passed, hide timer completely
      this.style.display = 'none';
      if (this.container) {
        this.container.removeAttribute('timed-release-status');
        this.container.classList.remove('product-timed-release');
      }
      return;
    }

    // Initialize HTML structure
    this.innerHTML = `
      <span data-upcoming class="timed-release__upcoming"></span>
      <span data-timer class="timed-release__timer">
        <span data-days>00</span>:<span data-hours>00</span>:<span data-minutes>00</span>:<span data-seconds>00</span>
      </span>
      <span data-status class="timed-release__status"></span>
    `;

    // Cache DOM elements
    this.elements = {
      upcoming: this.querySelector('[data-upcoming]'),
      timer: this.querySelector('[data-timer]'),
      status: this.querySelector('[data-status]'),
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]')
    };

    // Find container (product card or product page)
    this.container = this.closest('.product-timed-release') || 
                     this.closest('.product') || 
                     this;

    // Start countdown
    this.startCountdown();
  }

  disconnectedCallback() {
    // Clean up interval when component is removed
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  startCountdown() {
    const now = new Date().getTime();
    const distance = this.releaseTime - now;

    // If already expired (release time in the past), hide timer completely
    if (distance <= 0) {
      this.handleExpired();
      return;
    }

    // Release time is in the future - show countdown
    // Update immediately
    this.updateTimer(distance);

    // Update every second
    this.intervalId = setInterval(() => {
      const now = new Date().getTime();
      const distance = this.releaseTime - now;

      if (distance <= 0) {
        clearInterval(this.intervalId);
        this.handleExpired();
      } else {
        this.updateTimer(distance);
      }
    }, 1000);
  }

  updateTimer(distance) {
    // Calculate time units
    const days = Math.floor(distance / this.multipliers.day);
    const hours = Math.floor((distance % this.multipliers.day) / this.multipliers.hour);
    const minutes = Math.floor((distance % this.multipliers.hour) / this.multipliers.minute);
    const seconds = Math.floor((distance % this.multipliers.minute) / this.multipliers.second);

    // Update display
    if (this.elements.days) {
      this.elements.days.textContent = String(days).padStart(2, '0');
    }
    if (this.elements.hours) {
      this.elements.hours.textContent = String(hours).padStart(2, '0');
    }
    if (this.elements.minutes) {
      this.elements.minutes.textContent = String(minutes).padStart(2, '0');
    }
    if (this.elements.seconds) {
      this.elements.seconds.textContent = String(seconds).padStart(2, '0');
    }

    // Show timer and status
    if (this.elements.timer) {
      this.elements.timer.style.display = '';
    }
    if (this.elements.upcoming) {
      this.elements.upcoming.style.display = 'none';
    }
    
    // Show "(Upcoming)" status when release time is in the future
    if (this.elements.status) {
      this.elements.status.textContent = '(Upcoming)';
    }

    // Update status to "upcoming" when release time is in the future
    this.setAttribute('status', 'upcoming');
    if (this.container) {
      this.container.setAttribute('timed-release-status', 'upcoming');
    }
  }

  handleExpired() {
    // When expired, hide the timer completely and show as normal product
    // Clear timer display
    this.setTimerValues(0);

    // Hide the entire timer element
    this.style.display = 'none';
    
    // Remove timed-release attributes/classes to show as normal product
    if (this.container) {
      this.container.removeAttribute('timed-release-status');
      this.container.classList.remove('product-timed-release');
    }

    // Dispatch custom event for product page handler
    const event = new CustomEvent('timed-release:expired', {
      detail: {
        productId: this.productId,
        releaseTime: this.releaseTime
      },
      bubbles: true,
      cancelable: true
    });
    this.dispatchEvent(event);
    document.dispatchEvent(event);
  }

  setTimerValues(distance) {
    const days = Math.floor(distance / this.multipliers.day);
    const hours = Math.floor((distance % this.multipliers.day) / this.multipliers.hour);
    const minutes = Math.floor((distance % this.multipliers.hour) / this.multipliers.minute);
    const seconds = Math.floor((distance % this.multipliers.minute) / this.multipliers.second);

    if (this.elements.days) {
      this.elements.days.textContent = String(days).padStart(2, '0');
    }
    if (this.elements.hours) {
      this.elements.hours.textContent = String(hours).padStart(2, '0');
    }
    if (this.elements.minutes) {
      this.elements.minutes.textContent = String(minutes).padStart(2, '0');
    }
    if (this.elements.seconds) {
      this.elements.seconds.textContent = String(seconds).padStart(2, '0');
    }
  }
}

// Register the custom element
if (!customElements.get('timed-release-timer')) {
  customElements.define('timed-release-timer', TimedReleaseTimer);
}

